import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import useAuth from '../../../../lib/hooks/use-auth'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { StaffCommentInput } from '../../../../lib/types/models/staff-comment/staff-comment-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { StaffCommentForm } from '../../staffs/staff-comment-form'
import { COMMENTS_SECTION_ID } from '../utils/accordion-items'

const StaffCommentsSection = () => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    number
  >(['create', 'edit'])
  const { user } = useAuth()

  const form = useFormContext<AdminStaffInput>()
  const comments = form.watch('comments')

  const columns: Column<IdInput<StaffCommentInput>>[] = [
    {
      enableSorting: true,
      header: 'Sujet',
      accessorKey: 'subject',
      cell: ({ row }) => valueRender(row.original.subject?.name)
    },
    {
      enableSorting: true,
      header: 'Commentaire',
      accessorKey: 'comment'
    },
    {
      enableSorting: true,
      header: 'Commenté par',
      accessorKey: 'commentedByName'
    },
    {
      enableSorting: true,
      header: 'Planning',
      accessorKey: 'displayInPlanning',
      cell: ({ row }) => (row.original.displayInPlanning ? 'Oui' : 'Non')
    },
    {
      enableSorting: true,
      header: 'Visibilité',
      accessorKey: 'displayForDirector',
      cell: ({ row }) => (row.original.displayForDirector ? 'Indéfiniment' : 'Année en cours')
    },
    {
      enableSorting: true,
      header: 'Date & heure',
      accessorKey: 'date',
      cell: ({ row }) => (
        <Badge text={DateFormatter.formatDateHour(row.original.date)} variant="grey" />
      )
    },
    {
      enableSorting: false,
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original.id)
            }}
          />

          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() =>
              form.setValue(
                'comments',
                comments.filter((_, index) => index !== row.original.id)
              )
            }
          />
        </div>
      )
    }
  ]

  return (
    <div className="p-5 bg-primary-light rounded-2xl" id={COMMENTS_SECTION_ID}>
      <div className="flex items-center justify-between">
        <Typography variant="h6">Commentaires</Typography>
        <Button
          type="button"
          icon={PlusIcon}
          label={'Ajouter un commentaire'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      </div>
      <div className="h-3" />
      <div className="bg-white rounded-3xl">
        <DataTable<IdInput<StaffCommentInput>>
          manualSorting={false}
          variant="white"
          columns={columns}
          data={comments.map((comment, index) => ({ ...comment, id: index }))}
          rowCount={comments.length}
          paginated={false}
        />
      </div>

      <Modal
        isOpen={modalState.create}
        title={'Écrire un commentaire'}
        icon={MessageIcon}
        hideActions
        onCancel={() => closeModel('create')}
      >
        <StaffCommentForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            commentedByName: toFullName(user.firstName, user.lastName),
            date: new Date()
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: (values) => {
              form.setValue('comments', [...comments, values])
              closeModel('create')
            }
          })}
        />
      </Modal>

      {selectedData !== undefined && (
        <Modal
          isOpen={modalState.edit}
          title={'Éditer un commentaire'}
          icon={MessageIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <StaffCommentForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={comments[selectedData]}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: (values) => {
                form.setValue('comments', [
                  ...comments.slice(0, selectedData),
                  values,
                  ...comments.slice(selectedData + 1)
                ])
                closeModel('edit')
              }
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default StaffCommentsSection
